import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import { Box, Button } from "@mui/material";
import { useMemo, useRef, useState } from "react";
import Flex from "@/components/Flex";
import LoadingOverlay from "@/components/LoadingOverlay";
import { PdfGenerator } from "@/components/PdfGenerator";
import StyledDialog from "@/components/StyledDialog";
import Text from "@/components/Text";
import { TENANT_FEATURE } from "@/constants/tenant-feature-constant";
import { DisclosureHost } from "@/hooks/use-disclosure";
import { useTenantFeatures } from "@/hooks/use-tenant-features";
import { useSnackbar } from "@/hooks/useSnackbar";
import { getErrorSnackbar } from "@/utils/utils";
import IncludeInReportPanel from "./components/IncludeInReportPanel";
import NotePreviewDocument from "./components/NotePreviewDocument";
import { useInlineReportImages } from "./hooks/use-inline-report-images";
import { useNotePreviewSections } from "./hooks/use-note-preview-sections";
import { useScreeningResults } from "./hooks/use-screening-results";
import { useReportSelection } from "./hooks/use-report-selection";
import {
  NOTE_PREVIEW_CANVAS_BACKGROUND,
  NOTE_PREVIEW_DIALOG_WIDTH,
  NOTE_PREVIEW_DIVIDER_COLOR,
  NOTE_PREVIEW_LABELS,
} from "./note-preview-constant";
import { buildPdfFileName, downloadBlob } from "./note-preview-file-utils";
import { enrichScreeningSections } from "./note-preview-screening-utils";
import { buildNotePreviewView } from "./note-preview-utils";

export interface NotePreviewModalProps {
  /** Opened with the appointment id: `disclosure.open(appointmentId)`. */
  disclosure: DisclosureHost;
  onClose?: () => void;
}

const NotePreviewModal = ({ disclosure, onClose }: NotePreviewModalProps) => {
  const appointmentId: string | undefined = disclosure.data;
  const showSnackbar = useSnackbar();
  const pdfRef = useRef<{ generatePdf: (name?: string) => void }>(null);
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);

  const {
    report: rawReport,
    sections,
    isLoading,
    error,
  } = useNotePreviewSections(appointmentId, disclosure.opened);
  const { report, isResolvingImages } = useInlineReportImages(rawReport);
  const screening = useScreeningResults(appointmentId, disclosure.opened);
  const enrichedSections = useMemo(
    () => enrichScreeningSections(sections, screening.results),
    [sections, screening.results]
  );
  const selection = useReportSelection(enrichedSections);
  const { isEnabled } = useTenantFeatures();
  const showEmrLogo = isEnabled(TENANT_FEATURE.PREMIER_MD_BRANDING);
  const view = useMemo(
    () => buildNotePreviewView(report, showEmrLogo),
    [report, showEmrLogo]
  );
  const pdfFileName = useMemo(() => buildPdfFileName(report), [report]);
  const dialogTitle = [NOTE_PREVIEW_LABELS.dialogTitle, view.footer.dosLine]
    .filter(Boolean)
    .join(NOTE_PREVIEW_LABELS.dialogTitleSeparator);

  const close = () => (onClose ? onClose() : disclosure.close());

  const handleDownload = () => {
    try {
      setIsGeneratingPdf(true);
      pdfRef.current?.generatePdf(pdfFileName);
    } catch (err) {
      setIsGeneratingPdf(false);
      showSnackbar(getErrorSnackbar(err));
    }
  };

  if (!disclosure.opened) return null;

  return (
    <>
      <StyledDialog
        open={disclosure.opened}
        onClose={close}
        PaperProps={{
          sx: { width: NOTE_PREVIEW_DIALOG_WIDTH, maxWidth: "none" },
        }}
        dialogTitle={
          <Flex alignItems="center" gap={1}>
            <DescriptionOutlinedIcon fontSize="small" />
            <Text fontSize="16px" fontWeight="medium">
              {dialogTitle}
            </Text>
          </Flex>
        }
        endIcon={
          <Button
            variant="outlined"
            size="small"
            startIcon={<DownloadOutlinedIcon />}
            disabled={
              !selection.selectedSections.length ||
              isLoading ||
              isResolvingImages ||
              screening.isLoading
            }
            onClick={handleDownload}
          >
            {NOTE_PREVIEW_LABELS.download}
          </Button>
        }
      >
        <LoadingOverlay
          isLoading={
            isLoading ||
            isResolvingImages ||
            screening.isLoading ||
            isGeneratingPdf
          }
        />
        {error ? (
          <Text c="error.main">{NOTE_PREVIEW_LABELS.loadError}</Text>
        ) : (
          <Flex
            gap={2}
            alignItems="flex-start"
            sx={{
              bgcolor: NOTE_PREVIEW_CANVAS_BACKGROUND,
              borderRadius: 2,
              p: 2,
            }}
          >
            <IncludeInReportPanel
              sections={selection.sections}
              areAllSelected={selection.areAllSelected}
              isSelected={selection.isSelected}
              onToggle={selection.toggle}
              onSelectAll={selection.selectAll}
              onClearAll={selection.clearAll}
            />
            <Box
              sx={{
                flex: 1,
                minWidth: 0,
                bgcolor: "#FFFFFF",
                border: `1px solid ${NOTE_PREVIEW_DIVIDER_COLOR}`,
                borderRadius: 2,
                overflow: "hidden",
              }}
            >
              <NotePreviewDocument
                view={view}
                sections={selection.selectedSections}
              />
            </Box>
          </Flex>
        )}
      </StyledDialog>

      <PdfGenerator
        ref={pdfRef}
        onSave={(blob: Blob | null) => {
          setIsGeneratingPdf(false);
          if (blob) downloadBlob(blob, pdfFileName);
        }}
      >
        <NotePreviewDocument
          view={view}
          sections={selection.selectedSections}
        />
      </PdfGenerator>
    </>
  );
};

export default NotePreviewModal;
